import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Circle,
  GoogleMap,
  InfoWindow,
  Marker,
  Polygon,
  useJsApiLoader,
} from '@react-google-maps/api';
import { Geofence, GeofenceShapeType, LatLng } from '@models/geofence.model';
import { DEFAULT_GEOFENCE_COLOR, getGeofenceMarkerIcon } from '@utils/geofenceCategoryIcons';
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
  GOOGLE_MAPS_VERSION,
} from '@utils/googleMapsConfig';

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;
const SELECTED_ZOOM = 15;

export interface WorldMapProjectMarker {
  projectId: string;
  name: string;
  lat: number;
  lng: number;
}

interface WorldMapProps {
  projects: WorldMapProjectMarker[];
  geofences: Geofence[];
  /** Project IDs that already have a geofence — their marker is hidden in favor of the shape. */
  projectIdsWithGeofence?: Set<string>;
  /** Optional override: if provided, the map pans to this position when it changes. */
  panTo?: LatLng | null;
  selectedProjectId?: string | null;
  selectedGeofenceId?: string | null;
  showProjects?: boolean;
  showGeofences?: boolean;
  onSelectProject?: (project: WorldMapProjectMarker) => void;
  onSelectGeofence?: (geofence: Geofence) => void;
  /** Custom content rendered inside the project marker InfoWindow */
  renderProjectInfoWindow?: (project: WorldMapProjectMarker) => ReactNode;
  /** Custom content rendered inside the geofence InfoWindow when one is clicked */
  renderGeofenceInfoWindow?: (geofence: Geofence) => ReactNode;
  size?: { width: string; height: string };
  /** Padding (in px) applied to fitBounds. Smaller for tight modal maps. */
  fitPadding?: number;
}

const buildShapeOptions = (color: string, isSelected: boolean) => ({
  fillColor: color,
  fillOpacity: isSelected ? 0.35 : 0.2,
  strokeColor: color,
  strokeOpacity: isSelected ? 0.95 : 0.7,
  strokeWeight: isSelected ? 3 : 2,
  clickable: true,
  zIndex: isSelected ? 2 : 1,
});

const computeGeofenceCenter = (geofence: Geofence): LatLng | null => {
  if (geofence.shape.type === GeofenceShapeType.POINT_RADIUS) {
    return geofence.shape.center ?? null;
  }
  const coords = geofence.shape.polygonCoordinates;
  if (!coords || coords.length === 0) return null;
  let latSum = 0;
  let lngSum = 0;
  coords.forEach((p) => {
    latSum += p.lat;
    lngSum += p.lng;
  });
  return { lat: latSum / coords.length, lng: lngSum / coords.length };
};

export const WorldMap: React.FC<WorldMapProps> = ({
  projects,
  geofences,
  projectIdsWithGeofence,
  panTo,
  selectedProjectId,
  selectedGeofenceId,
  showProjects = true,
  showGeofences = true,
  onSelectProject,
  onSelectGeofence,
  renderProjectInfoWindow,
  renderGeofenceInfoWindow,
  size = { width: '100%', height: '100%' },
  fitPadding = 60,
}) => {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [openGeofenceId, setOpenGeofenceId] = useState<string | null>(null);

  const visibleGeofences = useMemo(() => {
    if (!showGeofences) return [];
    return geofences.filter(
      (g) => g.shape && (g.shape.center || g.shape.polygonCoordinates),
    );
  }, [geofences, showGeofences]);

  // Sprint 16, story-077: only hide a project's own marker when its geofence's marker will
  // actually render in its place — never trust projectIdsWithGeofence blindly for a geofence
  // with no resolvable center (e.g. a malformed POLYGON with no coordinates), which would
  // otherwise leave that project with neither a project pin nor a geofence pin. This is a strict
  // tightening (fewer projects ever get hidden, never more) of the existing hide-for-polygon rule.
  const projectIdsWithVisiblePin = useMemo(
    () =>
      new Set(
        visibleGeofences
          .filter((g) => g.projectId && computeGeofenceCenter(g) !== null)
          .map((g) => g.projectId as string),
      ),
    [visibleGeofences],
  );

  // Hide the marker of projects whose geofence will render its own marker instead.
  const visibleProjects = useMemo(() => {
    if (!showProjects) return [];
    if (!projectIdsWithGeofence || projectIdsWithGeofence.size === 0) return projects;
    return projects.filter(
      (p) => !(projectIdsWithGeofence.has(p.projectId) && projectIdsWithVisiblePin.has(p.projectId)),
    );
  }, [projects, projectIdsWithGeofence, projectIdsWithVisiblePin, showProjects]);

  const fitMapToContents = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasAny = false;

    visibleProjects.forEach((p) => {
      if (
        typeof p.lat === 'number' &&
        typeof p.lng === 'number' &&
        Number.isFinite(p.lat) &&
        Number.isFinite(p.lng)
      ) {
        bounds.extend(new window.google.maps.LatLng(p.lat, p.lng));
        hasAny = true;
      }
    });

    visibleGeofences.forEach((g) => {
      // Extend bounds with the full shape area so fitBounds zooms out enough
      // to show the whole geofence:
      //  - POINT_RADIUS: 4 corners of the bounding box (centre ± delta)
      //  - POLYGON: every vertex
      if (
        g.shape.type === GeofenceShapeType.POINT_RADIUS &&
        g.shape.center &&
        g.shape.radiusMeters
      ) {
        const { lat, lng } = g.shape.center;
        const r = g.shape.radiusMeters;
        const latDelta = r / 111320;
        const lngDelta = r / (111320 * Math.cos((lat * Math.PI) / 180));
        bounds.extend(new window.google.maps.LatLng(lat - latDelta, lng - lngDelta));
        bounds.extend(new window.google.maps.LatLng(lat + latDelta, lng + lngDelta));
        hasAny = true;
      } else if (
        g.shape.type === GeofenceShapeType.POLYGON &&
        g.shape.polygonCoordinates
      ) {
        g.shape.polygonCoordinates.forEach((p) => {
          bounds.extend(new window.google.maps.LatLng(p.lat, p.lng));
          hasAny = true;
        });
      }
    });

    if (!hasAny) {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
      return;
    }

    map.fitBounds(bounds, {
      top: fitPadding,
      bottom: fitPadding,
      left: fitPadding,
      right: fitPadding,
    });
  }, [visibleProjects, visibleGeofences, fitPadding]);

  const onLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      mapRef.current = mapInstance;
      fitMapToContents();
    },
    [fitMapToContents],
  );

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // Re-fit when contents change
  useEffect(() => {
    fitMapToContents();
  }, [fitMapToContents]);

  // External pan
  const panToLat = panTo?.lat;
  const panToLng = panTo?.lng;
  useEffect(() => {
    if (panToLat == null || panToLng == null || !mapRef.current) return;
    mapRef.current.panTo({ lat: panToLat, lng: panToLng });
    const z = mapRef.current.getZoom() ?? DEFAULT_ZOOM;
    if (z < SELECTED_ZOOM - 2) mapRef.current.setZoom(SELECTED_ZOOM);
  }, [panToLat, panToLng]);

  // Pan to selection
  useEffect(() => {
    if (!mapRef.current) return;
    if (selectedGeofenceId) {
      const g = visibleGeofences.find((x) => x.geofenceId === selectedGeofenceId);
      const center = g ? computeGeofenceCenter(g) : null;
      if (center) {
        mapRef.current.panTo(center);
        const z = mapRef.current.getZoom() ?? DEFAULT_ZOOM;
        if (z < SELECTED_ZOOM - 2) mapRef.current.setZoom(SELECTED_ZOOM);
      }
    } else if (selectedProjectId) {
      const p = visibleProjects.find((x) => x.projectId === selectedProjectId);
      if (p) {
        mapRef.current.panTo({ lat: p.lat, lng: p.lng });
        const z = mapRef.current.getZoom() ?? DEFAULT_ZOOM;
        if (z < SELECTED_ZOOM - 2) mapRef.current.setZoom(SELECTED_ZOOM);
      }
    }
  }, [selectedProjectId, selectedGeofenceId, visibleProjects, visibleGeofences]);

  if (!isLoaded) {
    return <div style={size} className="bg-gray-50 animate-pulse" />;
  }

  return (
    <GoogleMap
      mapContainerStyle={size}
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        zoomControl: true,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
      }}
    >
      {visibleProjects.map((project) => (
        <Marker
          key={project.projectId}
          position={{ lat: project.lat, lng: project.lng }}
          onClick={() => {
            setOpenProjectId(project.projectId);
            setOpenGeofenceId(null);
            onSelectProject?.(project);
          }}
        >
          {renderProjectInfoWindow && openProjectId === project.projectId && (
            <InfoWindow
              position={{ lat: project.lat, lng: project.lng }}
              onCloseClick={() => setOpenProjectId(null)}
            >
              <>{renderProjectInfoWindow(project)}</>
            </InfoWindow>
          )}
        </Marker>
      ))}

      {visibleGeofences.map((geofence) => {
        const color =
          geofence.category?.color || geofence.color || DEFAULT_GEOFENCE_COLOR;
        const isSelected = geofence.geofenceId === selectedGeofenceId;
        const opts = buildShapeOptions(color, isSelected);
        const center = computeGeofenceCenter(geofence);

        const handleClick = () => {
          setOpenGeofenceId(geofence.geofenceId);
          setOpenProjectId(null);
          onSelectGeofence?.(geofence);
        };

        // Rendered as a child of the new Marker below (when a center resolves) rather than as a
        // sibling of the Circle/Polygon, so only one InfoWindow ever mounts for the same
        // openGeofenceId (sprint 16, story-077).
        const infoWindow =
          renderGeofenceInfoWindow &&
          openGeofenceId === geofence.geofenceId &&
          center ? (
            <InfoWindow
              position={center}
              onCloseClick={() => setOpenGeofenceId(null)}
            >
              <>{renderGeofenceInfoWindow(geofence)}</>
            </InfoWindow>
          ) : null;

        // Sprint 16, story-077: a fixed-size marker at the geofence's center, in addition to its
        // Circle/Polygon shape, so the geofence stays findable and clickable at any zoom — the
        // shape alone can shrink below visibility when zoomed out (2BDYOji2). Same click handler
        // as the shape; this is an additional hit target for the identical interaction, not a new
        // one.
        const marker = center ? (
          <Marker
            key={`${geofence.geofenceId}-pin`}
            position={center}
            icon={getGeofenceMarkerIcon(geofence.category?.icon, color)}
            onClick={handleClick}
            zIndex={isSelected ? 3 : 2}
          >
            {infoWindow}
          </Marker>
        ) : null;

        if (
          geofence.shape.type === GeofenceShapeType.POINT_RADIUS &&
          geofence.shape.center &&
          geofence.shape.radiusMeters
        ) {
          return (
            <React.Fragment key={geofence.geofenceId}>
              <Circle
                center={geofence.shape.center}
                radius={geofence.shape.radiusMeters}
                options={opts}
                onClick={handleClick}
              />
              {marker}
            </React.Fragment>
          );
        }

        if (
          geofence.shape.type === GeofenceShapeType.POLYGON &&
          geofence.shape.polygonCoordinates &&
          geofence.shape.polygonCoordinates.length >= 3
        ) {
          return (
            <React.Fragment key={geofence.geofenceId}>
              <Polygon
                paths={geofence.shape.polygonCoordinates}
                options={opts}
                onClick={handleClick}
              />
              {marker}
            </React.Fragment>
          );
        }

        // Neither shape branch matched (e.g. a POLYGON with fewer than 3 coordinates) — still
        // render the marker alone if a center resolved, so this project is never left with zero
        // visible pins (the same guarantee `projectIdsWithVisiblePin` makes above).
        return marker ? (
          <React.Fragment key={geofence.geofenceId}>{marker}</React.Fragment>
        ) : null;
      })}
    </GoogleMap>
  );
};

export default WorldMap;
